import { DEFAULT_IMAGE, RESIZER_PROXY } from "../globals";
import { getDeviceTypeAkamai } from "../../common/utils";
import { PROMO_IMAGE_WIDTHS, PROMO_IMAGE_SIZES } from "./imageConstants";

export function imageResizer(url = DEFAULT_IMAGE, width = 960, height = null) {
  let resizerUrl = `${RESIZER_PROXY}?src=${encodeURIComponent(url)}&w=${width}`;

  if (height) {
    resizerUrl += `&h=${height}`;
  }

  return resizerUrl;
}

export function buildImageSrcSet(url, widths, height = null) {
  return widths.map(w => `${imageResizer(url, w, height)} ${w}w`).join(", ");
}

export function buildPromoImageProps(baseUrl, isVertical, height = null) {
  const widths = isVertical
    ? PROMO_IMAGE_WIDTHS.vertical
    : PROMO_IMAGE_WIDTHS.horizontal;
  const sizes = isVertical
    ? PROMO_IMAGE_SIZES.vertical
    : PROMO_IMAGE_SIZES.horizontal;
  const fallbackWidth = widths[widths.length - 1];

  return {
    src: height
      ? imageResizer(baseUrl, fallbackWidth, height)
      : imageResizer(baseUrl, fallbackWidth),
    srcSet: buildImageSrcSet(baseUrl, widths, height),
    sizes
  };
}

export const getStreamsByTypeAndBitrate = (
  streams,
  stream_type,
  maxBitrate
) => {
  return streams
    .filter(
      stream =>
        stream["bitrate"] <= maxBitrate && stream["stream_type"] === stream_type
    )
    .reduce((prev, current) => {
      return prev?.bitrate > current?.bitrate ? prev : current;
    }, undefined);
};

/**
 * Returns the maximum bitrate for a given device type
 * @param {array} streams of possile streams
 * @returns  the stream that matches the device type
 */
export const getVideoStream = (streams, forceHD = false) => {
  // let hls handle level selection based on device bandwidth
  // capLevelToPlayerSize is set to true in useHls and should prevent mobile devices from receiving bitrates that are too high
  const tsStream = streams
    .filter(
      stream =>
        (stream.stream_type === "ts") &
        !stream.url?.endsWith("hlsv4_master.m3u8")
    )
    .reduce((prev, current) => {
      return prev?.bitrate > current?.bitrate ? prev : current;
    }, undefined);

  if (!tsStream) {
    const mp4Stream = getMp4VideoStream(
      streams,
      getDeviceTypeAkamai(),
      forceHD
    );
    return mp4Stream;
  }

  return tsStream;
};

export const getVideoStreamForVerticalVideoThumbnail = streams => {
  return getStreamsByTypeAndBitrate(streams, "mp4", 600) || streams?.[0];
};

/**
 * Add leading digits to a number until it has at least 2 digits
 * @param {number} num the number to pad
 * @returns {string} padded number (with leading zeroes)
 */
function padTo2Digits(num) {
  return num.toString().padStart(2, "0");
}

/**
 * Given a duration in milliseconds, convert it to hh:mm:ss format
 * @param {number} milliseconds time in milliseconds
 * @returns {object} contains 2 properties:
 * visible: the milliseconds formatted as hh:mm:ss (or mm:ss if hours is 0)
 * srOnly: the milliseconds formatted as "X hours, Y minutes and Z seconds"
 * (or Y minutes and Z seconds, if hours is 0) for screen readers
 */
export function formatHoursMinutesSeconds(milliseconds) {
  if (!milliseconds) {
    return { visible: "", srOnly: "" };
  }

  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  seconds %= 60;
  minutes %= 60;

  const visibleFormat = hours
    ? `${hours}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`
    : `${minutes}:${padTo2Digits(seconds)}`;

  const screenReaderFormat = hours
    ? `${hours} hours, ${padTo2Digits(minutes)} minutes and ${padTo2Digits(
        seconds
      )} seconds`
    : `${minutes} minutes and ${padTo2Digits(seconds)} seconds`;

  return { visible: visibleFormat, srOnly: screenReaderFormat };
}

export const getMp4VideoStream = (streams, deviceType, forceHD = false) => {
  const maxMp4Bitrate = forceHD
    ? 5400
    : deviceType === "ios" || deviceType === "android"
    ? 600
    : 2000;

  const mp4Stream = getStreamsByTypeAndBitrate(streams, "mp4", maxMp4Bitrate);
  // if found, return mp4 stream that's fits the max bitrate
  return mp4Stream;
};
